<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visible"
    width="800px"
    top="5vh"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    custom-class="house-detail-dialog"
  >
    <!-- 房屋基本信息 -->
    <el-card shadow="never" class="detail-section">
      <div slot="header" class="clearfix">
        <span class="section-title">
          <i class="el-icon-house"></i> 基本信息
        </span>
      </div>
      
      <el-row :gutter="24">
        <!-- 基础信息列 -->
        <el-col :span="12">
          <div class="detail-item">
            <div class="detail-label">房屋编号：</div>
            <div class="detail-value">{{ houseData.houseid || '-' }}</div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">区域：</div>
            <div class="detail-value">
              <span class="address-value">
                <i class="el-icon-location-outline"></i>
                {{ houseData.address || '-' }}
              </span>
            </div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">楼栋号：</div>
            <div class="detail-value">
              <el-tag size="small" type="primary">
                {{ houseData.building || '-' }}
              </el-tag>
            </div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">房间号：</div>
            <div class="detail-value">
              <el-tag size="small" effect="plain">
                {{ houseData.roomNum || '-' }}
              </el-tag>
            </div>
          </div>
        </el-col>
        
        <!-- 状态和面积列 -->
        <el-col :span="12">
          <div class="detail-item">
            <div class="detail-label">房屋状态：</div>
            <div class="detail-value">
              <el-tag 
                :type="getStatusType(houseData.status)" 
                size="medium"
                effect="dark"
              >
                {{ houseData.status || '未设置' }}
              </el-tag>
            </div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">房屋面积：</div>
            <div class="detail-value">
              <span class="number-value">
                {{ formatNumber(houseData.roomSquare) }} ㎡
              </span>
            </div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">房屋价格：</div>
            <div class="detail-value">
              <span class="price-value">
                <i class="el-icon-money"></i>
                {{ formatCurrency(houseData.price) }} 元
              </span>
            </div>
          </div>
          
          <div class="detail-item">
            <div class="detail-label">创建时间：</div>
            <div class="detail-value time-value">
              {{ formatDateTime(houseData.createTime) }}
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>
    
    <!-- 新增：表号信息卡片 -->
    <el-card shadow="never" class="detail-section">
      <div slot="header" class="clearfix">
        <span class="section-title">
          <i class="el-icon-s-tools"></i> 表号信息
        </span>
      </div>
      
      <el-row :gutter="24">
        <el-col :span="8">
          <div class="meter-item">
            <div class="meter-label">
              <i class="el-icon-water-cup"></i> 水表号：
            </div>
            <div class="meter-value">{{ houseData.waterMeterNumber || '-' }}</div>
          </div>
        </el-col>
        
        <el-col :span="8">
          <div class="meter-item">
            <div class="meter-label">
              <i class="el-icon-lightning"></i> 电表号：
            </div>
            <div class="meter-value">{{ houseData.meterNumber || '-' }}</div>
          </div>
        </el-col>
        
        <el-col :span="8">
          <div class="meter-item">
            <div class="meter-label">
              <i class="el-icon-gas-station"></i> 燃气表号：
            </div>
            <div class="meter-value">{{ houseData.gasMeterNumber || '-' }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>
    
    <!-- 房屋详情 -->
    <el-card shadow="never" class="detail-section">
      <div slot="header" class="clearfix">
        <span class="section-title">
          <i class="el-icon-document"></i> 房屋详情
        </span>
      </div>
      
      <div class="detail-content">
        <div v-if="houseData.detail" class="detail-text">
          {{ houseData.detail }}
        </div>
        <div v-else class="empty-content">
          <i class="el-icon-document"></i>
          暂无房屋详情描述
        </div>
      </div>
    </el-card>
    
    <!-- 操作按钮 -->
    <div slot="footer" class="dialog-footer">
      <div class="footer-right">
        <el-button 
          size="small" 
          icon="el-icon-close"
          @click="handleClose"
        >
          关闭
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'HouseDetailDialog',
  
  props: {
    // 是否显示对话框
    visible: {
      type: Boolean,
      default: false
    },
    
    // 房屋数据
    houseData: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      // 空数据占位符
    }
  },
  
  computed: {
    // 对话框标题
    dialogTitle() {
      const address = this.houseData.address || ''
      const building = this.houseData.building || ''
      const roomNum = this.houseData.roomNum || ''
      
      if (address && building && roomNum) {
        return `房屋详情 - ${address} ${building}栋 ${roomNum}室`
      } else if (this.houseData.houseid) {
        return `房屋详情 - ${this.houseData.houseid}`
      }
      return '房屋详情'
    },
    
    // 房屋完整地址
    fullAddress() {
      const parts = []
      if (this.houseData.address) parts.push(this.houseData.address)
      if (this.houseData.building) parts.push(`${this.houseData.building}栋`)
      if (this.houseData.roomNum) parts.push(`${this.houseData.roomNum}室`)
      
      return parts.join(' ')
    }
  },
  
  watch: {
    // 监听对话框可见性变化
    visible(newVal) {
      if (newVal) {
        this.$emit('opened', this.houseData)
      }
    }
  },
  
  methods: {
    /**
     * 获取状态标签类型
     */
    getStatusType(status) {
      const statusMap = {
        '已出租': 'success',
        '未出租': 'info',
        '已出售': 'warning',
        '维修中': 'danger'
      }
      return statusMap[status] || 'info'
    },
    
    /**
     * 格式化数字
     */
    formatNumber(value) {
      if (value == null || value === '' || isNaN(Number(value))) {
        return '-'
      }
      return Number(value).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    /**
     * 格式化货币
     */
    formatCurrency(value) {
      if (value == null || value === '' || isNaN(Number(value))) {
        return '-'
      }
      return Number(value).toLocaleString('zh-CN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },
    
    /**
     * 格式化日期时间
     */
    formatDateTime(value) {
      if (!value) return '-'
      
      // 如果是时间戳，转换为日期格式
      if (typeof value === 'number') {
        const date = new Date(value)
        return date.toLocaleString('zh-CN')
      }
      
      // 如果是字符串，直接显示
      return value
    },
    
    /**
     * 处理编辑
     */
    handleEdit() {
      this.$emit('edit', this.houseData)
      this.handleClose()
    },
    
    /**
     * 处理删除
     */
    handleDelete() {
      this.$confirm('确定要删除这个房屋吗？', '删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger',
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            instance.confirmButtonLoading = true
            this.$emit('delete', this.houseData.houseid)
            done()
          } else {
            done()
          }
        }
      }).then(() => {
        this.handleClose()
      }).catch(() => {
        // 用户取消
      })
    },
    
    /**
     * 处理关闭
     */
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* 对话框自定义样式 */
.house-detail-dialog .el-dialog__body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.house-detail-dialog .el-dialog__footer {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
}

.detail-section {
  margin-bottom: 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.section-title i {
  margin-right: 8px;
  color: #409EFF;
  font-size: 18px;
}

/* 详情项样式 */
.detail-item {
  display: flex;
  margin-bottom: 16px;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
}

.detail-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.detail-label {
  width: 100px;
  font-size: 14px;
  color: #909399;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  word-break: break-word;
}

/* 新增：表号信息样式 */
.meter-item {
  padding: 12px 8px;
  text-align: center;
  border-radius: 6px;
  background-color: #f8f9fa;
  margin-bottom: 8px;
}

.meter-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.meter-label i {
  margin-right: 6px;
  font-size: 16px;
}

/* 表号图标颜色区分 */
.meter-label .el-icon-water-cup {
  color: #409EFF; /* 蓝色 - 水表 */
}

.meter-label .el-icon-lightning {
  color: #e6a23c; /* 黄色 - 电表 */
}

.meter-label .el-icon-gas-station {
  color: #f56c6c; /* 红色 - 燃气表 */
}

.meter-value {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  font-family: 'Arial', sans-serif;
  letter-spacing: 1px;
}

/* 特殊值样式 */
.address-value {
  display: flex;
  align-items: center;
  color: #409EFF;
}

.address-value i {
  margin-right: 6px;
  font-size: 16px;
}

.number-value {
  font-family: 'Arial', sans-serif;
  font-weight: 600;
  color: #e6a23c;
}

.price-value {
  display: flex;
  align-items: center;
  font-family: 'Arial', sans-serif;
  font-weight: 600;
  color: #f56c6c;
}

.price-value i {
  margin-right: 6px;
  font-size: 16px;
}

.time-value {
  color: #909399;
  font-size: 13px;
}

/* 详情内容样式 */
.detail-content {
  padding: 8px 0;
}

.detail-text {
  line-height: 1.8;
  font-size: 14px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #c0c4cc;
  font-size: 14px;
}

.empty-content i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #dcdfe6;
}

/* 页脚按钮样式 */
.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.footer-right {
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: flex-end;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .house-detail-dialog .el-dialog {
    width: 95% !important;
    margin-top: 5vh !important;
  }
  
  .el-col {
    width: 100%;
  }
  
  /* 移动端表号信息单列显示 */
  .meter-item {
    margin-bottom: 12px;
    text-align: left;
    display: flex;
    align-items: center;
    padding: 10px 15px;
  }
  
  .meter-label {
    width: 80px;
    justify-content: flex-start;
    flex-shrink: 0;
  }
  
  .meter-value {
    flex: 1;
  }
  
  .dialog-footer {
    flex-direction: column;
    gap: 12px;
  }
  
  .footer-right {
    width: 100%;
    justify-content: center;
  }
}
</style>

<style>
/* 全局样式覆盖 */
.house-detail-dialog .el-dialog__header {
  padding: 20px 20px 10px;
  border-bottom: 1px solid #ebeef5;
}

.house-detail-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.house-detail-dialog .el-dialog__title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 18px;
  background: #409EFF;
  border-radius: 2px;
  margin-right: 8px;
}

.house-detail-dialog .el-card__header {
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #ebeef5;
}

.house-detail-dialog .el-card__body {
  padding: 20px;
}

/* 标签样式覆盖 */
.house-detail-dialog .el-tag {
  font-weight: 500;
  border-radius: 12px;
  padding: 0 12px;
  height: 28px;
  line-height: 28px;
}

.house-detail-dialog .el-tag--medium {
  padding: 0 14px;
  height: 32px;
  line-height: 32px;
  font-size: 14px;
}

/* 按钮样式优化 */
.house-detail-dialog .el-button--small {
  padding: 8px 15px;
  font-size: 13px;
}

.house-detail-dialog .el-button--small i {
  margin-right: 4px;
  font-size: 14px;
}

/* 滚动条样式 */
.house-detail-dialog .el-dialog__body::-webkit-scrollbar {
  width: 6px;
}

.house-detail-dialog .el-dialog__body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.house-detail-dialog .el-dialog__body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.house-detail-dialog .el-dialog__body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 动画效果 */
.house-detail-dialog .el-dialog {
  animation: dialog-fade-in 0.3s ease;
}

@keyframes dialog-fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 卡片悬停效果 */
.house-detail-dialog .el-card {
  transition: all 0.3s ease;
}

.house-detail-dialog .el-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08);
}
</style>